import { setAccountFarm } from '../../../../actions/accountFarmAction';
import { setCurrentSector } from '../../../../actions/currentSectorActions';
import apiClient from '../../../../requests/api/apiClient';
import {
  FarmSchema,
  SectorDetailSchema,
  UpdateSectorMeasuresSchema,
  UpdateSectorSchema,
} from '../../../../requests/api/apiTypes';
import { FetchError } from '../../../types';
import { REQUIRED_UPDATE_SECTOR_EQ, REQUIRED_UPDATE_SECTOR_FIELDS } from '../types';

export interface IrrigationConfig {
  lineNumber?: string;
  rowDistance?: string;
  plantDistance?: string;
  plantsPerHectare?: string;
  emittersPerHectare?: string;
  emitterSurface?: string;
  realPrecipitation?: string;
  designPrecipitation?: string;
  typeOfIrrigation?: string;
  theoreticalPrecipitation?: string;
  emitterFlow?: string;
  transmittersDistance?: number;
  lengthHileraFromCaudalimeter?: number;
  volumeCaudalimeter?: number;
  plantingFrame?: string;
}

export interface InheritedConfig {
  irrigationConfig: IrrigationConfig;
}

export const handleDeleteSector = async (
  sectorId: string,
  selectedFarm: FarmSchema | null,
  selectedSector: any,
  enqueueSnackbar: any,
  setDeleteModalOpen: (open: boolean) => void,
  dispatch: any,
  history: any,
) => {
  try {
    await apiClient.sector.sectorDelete(sectorId);
    enqueueSnackbar('Sector eliminado con éxito', { variant: 'success' });
    setDeleteModalOpen(false);
    if (selectedFarm) {
      const updatedFarm = {
        ...selectedFarm,
        sectors: selectedFarm.sectors?.filter(
          (sector) => sector.id !== sectorId,
        ) || [],
      };

      dispatch(setAccountFarm(updatedFarm));

      if (selectedSector && selectedSector.id && selectedSector.id === sectorId) {
        dispatch(setCurrentSector(null));
      }
    }
    history.push('/layout');
  } catch (err) {
    const { error } = err as FetchError;
    enqueueSnackbar(error.message, { variant: 'error' });
  }
};

export const handleUpdateSectorInformation = async (
  sectorInfo: SectorDetailSchema | undefined,
  originalSectorInfo: SectorDetailSchema | undefined,
  sectorId: string,
  selectedFarm: FarmSchema | null,
  brotherSectors: SectorDetailSchema[],
  enqueueSnackbar: any,
  setLoadingSaveSector: (loading: boolean) => void,
  dispatch: any,
  setSectorInfo: (info: any) => void,
  setInheritedConfig: (config: any) => void,
) => {
  if (!sectorInfo) {
    enqueueSnackbar('La información del sector no está disponible.', { variant: 'error' });
    return;
  }

  let originalSector = originalSectorInfo;
  if (!originalSector) {
    try {
      const { data } = await apiClient.sector.sectorDetail(sectorId);
      originalSector = data.sector;
      if (!originalSector) {
        enqueueSnackbar('No se pudo obtener la información del sector original', { variant: 'error' });
        return;
      }
    } catch (err) {
      enqueueSnackbar('Error al obtener la información del sector original', { variant: 'error' });
      return;
    }
  }

  if (sectorInfo.wiseconnId !== originalSector.wiseconnId) {
    const { data } = await apiClient.sectors.wiseconnIdsList();
    const otherSectorsIds = data.allSectorsWiseconnIds?.filter(
      (id: string) => id !== originalSector?.wiseconnId,
    );
    if (otherSectorsIds?.includes(sectorInfo.wiseconnId ?? '')) {
      enqueueSnackbar('Wiseconn ID ya está en uso', { variant: 'error' });
      return;
    }
  }

  const hasMissingFields = REQUIRED_UPDATE_SECTOR_FIELDS.some((field) => {
    if (!sectorInfo[field as keyof SectorDetailSchema]) {
      enqueueSnackbar(`El campo ${REQUIRED_UPDATE_SECTOR_EQ[field]} debe ser completado`, { variant: 'error' });
      return true;
    }
    return false;
  });

  if (hasMissingFields) return;

  const parentSectorChanged = sectorInfo.parentSector !== originalSector.parentSector;

  if (parentSectorChanged) {
    const currentSectorChildren = brotherSectors.filter(
      (sector) => sector.parentSector === sectorInfo.id,
    );
    if (currentSectorChildren.length > 0) {
      enqueueSnackbar(
        `No se puede cambiar el sector padre porque este sector tiene ${currentSectorChildren.length} sector(es) hijo(s). Primero debe quitar los sectores hijos.`,
        { variant: 'error' },
      );
      return;
    }
  }

  setLoadingSaveSector(true);
  try {
    const generalInfoFields = [
      'name',
      'theoreticalSurface',
      'sectorDevice',
      'wiseconnId',
      'typeOfSoil',
      'variety',
      'plantingYear',
      'plantingFrame',
      'managerName',
      'parentSector',
      'sectorNumber',
      'coordinates',
      'weatherZoneId',
      'sn',
      'telemetryType',
    ];

    const requestBody = Object.fromEntries(
      Object.entries(sectorInfo).filter(([key]) => generalInfoFields.includes(key)),
    ) as UpdateSectorSchema;

    const updatedRequestBody = {
      ...requestBody,
      weatherZoneId: sectorInfo.weatherZoneId,
      coordinates: sectorInfo.coordinates?.map((coord) => ({
        lat: Number(coord.lat),
        lng: Number(coord.lng),
      })),
      parentSector: sectorInfo.parentSector === '1' ? null : sectorInfo.parentSector,
      telemetryType: sectorInfo.telemetryType?.toLowerCase() || 'wiseconn',
    };
    const response = await apiClient.sector.sectorUpdate(sectorId, updatedRequestBody);

    try {
      await apiClient.sectors.individualSectorMeasuresUpdate(
        {
          farmId: selectedFarm?.id ?? '',
          wiseconnZoneId: sectorInfo.wiseconnId ?? '',
        },
        {} as UpdateSectorMeasuresSchema,
      );
    } catch (err) {
      console.log('Error adding measures', err);
    }

    if (response.data.sector) {
      const newFarmSectors = selectedFarm?.sectors?.map((sector) => {
        if (sector.id === sectorId) {
          return response.data.sector as typeof sector;
        }
        return sector;
      }) || [];

      dispatch(setAccountFarm({
        ...selectedFarm,
        sectors: newFarmSectors,
      } as FarmSchema));
      dispatch(setCurrentSector(response.data.sector));

      if (parentSectorChanged) {
        const updatedSector = response.data.sector;
        setSectorInfo(updatedSector);

        if (updatedSector.parentSector && updatedSector.parentSector !== '1') {
          const { data: parentData } = await apiClient.sector.sectorDetail(
            updatedSector.parentSector,
          );
          if (parentData?.sector) {
            const inheritedIrrigationConfig = {
              ...parentData.sector?.irrigationConfig,
            };

            setInheritedConfig({
              irrigationConfig: inheritedIrrigationConfig,
            });

            setSectorInfo((prev: any) => {
              if (!prev || !parentData.sector) return prev;
              return {
                ...prev,
                irrigationConfig: inheritedIrrigationConfig,
              };
            });

            // Guardar la configuración heredada en la base de datos
            try {
              const irrigationUpdateBody = {
                name: updatedSector.name,
                theoreticalSurface: updatedSector.theoreticalSurface,
                typeOfSoil: updatedSector.typeOfSoil,
                irrigationConfig: inheritedIrrigationConfig,
              } as UpdateSectorSchema;

              await apiClient.sector.sectorUpdate(sectorId, irrigationUpdateBody);
            } catch (err) {
              console.error('Error saving inherited irrigation config:', err);
              enqueueSnackbar('Error al guardar la configuración de riego heredada', { variant: 'warning' });
            }
          }
        } else {
          setInheritedConfig({ irrigationConfig: {} });
          setSectorInfo((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              irrigationConfig: {},
            };
          });

          try {
            const irrigationUpdateBody = {
              name: updatedSector.name,
              theoreticalSurface: updatedSector.theoreticalSurface,
              typeOfSoil: updatedSector.typeOfSoil,
              irrigationConfig: {},
            } as UpdateSectorSchema;

            await apiClient.sector.sectorUpdate(sectorId, irrigationUpdateBody);
          } catch (err) {
            console.error('Error clearing irrigation config:', err);
          }
        }
      }
    }

    enqueueSnackbar(response.data.message, { variant: 'success' });
  } catch (err) {
    const { error } = err as FetchError;
    enqueueSnackbar(error.message, { variant: 'error' });
  } finally {
    setLoadingSaveSector(false);
  }
};

export const handleUpdateIrrigationConfig = async (
  sectorInfo: SectorDetailSchema | undefined,
  sectorId: string,
  selectedFarm: FarmSchema | null,
  enqueueSnackbar: any,
  setLoadingSaveSector: (loading: boolean) => void,
  dispatch: any,
) => {
  if (!sectorInfo) {
    enqueueSnackbar('La información del sector no está disponible.', { variant: 'error' });
    return;
  }

  setLoadingSaveSector(true);
  try {
    const requestBody = {
      name: sectorInfo.name,
      theoreticalSurface: sectorInfo.theoreticalSurface,
      typeOfSoil: sectorInfo.typeOfSoil,
      irrigationConfig: sectorInfo.irrigationConfig || {},
    } as UpdateSectorSchema;

    const response = await apiClient.sector.sectorUpdate(sectorId, requestBody);

    if (response.data.sector) {
      const newFarmSectors = selectedFarm?.sectors?.map((sector) => {
        if (sector.id === sectorId) {
          return response.data.sector as typeof sector;
        }
        return sector;
      }) || [];

      dispatch(setAccountFarm({
        ...selectedFarm,
        sectors: newFarmSectors,
      } as FarmSchema));
      dispatch(setCurrentSector(response.data.sector));
    }

    enqueueSnackbar('Configuración de riego actualizada con éxito', { variant: 'success' });
  } catch (err) {
    const { error } = err as FetchError;
    enqueueSnackbar(error.message, { variant: 'error' });
  } finally {
    setLoadingSaveSector(false);
  }
};

export const getInheritedConfig = async (
  parentId: string,
  setInheritedConfig: (config: any) => void,
  setSectorInfo: (info: any) => void,
  force = false,
) => {
  if (!parentId || parentId === '1') {
    setInheritedConfig({ irrigationConfig: {} });
    return;
  }

  try {
    const { data } = await apiClient.sector.sectorDetail(parentId);
    const parentSector = data.sector;
    if (parentSector) {
      const config = {
        irrigationConfig: {
          ...parentSector.irrigationConfig,
        },
      };
      setInheritedConfig(config);
      if (force) {
        setSectorInfo((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            irrigationConfig: {
              ...parentSector.irrigationConfig,
            },
          };
        });
      }
    }
  } catch (err) {
    console.error('Error getting inherited config:', err);
  }
};

export const getSectorChildren = (
  sectorInfo: SectorDetailSchema | undefined,
  brotherSectors: SectorDetailSchema[],
) => {
  if (!sectorInfo?.id) return [];
  return brotherSectors.filter((sector) => sector.parentSector === sectorInfo.id);
};

export const getParentSectorHelperText = (sectorChildren: SectorDetailSchema[]) => {
  if (sectorChildren.length > 0) {
    return `Este sector tiene ${sectorChildren.length} sector(es) hijo(s): ${sectorChildren.map((s) => s.name).join(', ')}. Para asignar un sector padre, primero debe quitar los sectores hijos.`;
  }
  return '';
};

export const handleConfirmTabChange = (
  pendingTabChange: string | null,
  setValue: (value: string) => void,
  setPendingTabChange: (value: string | null) => void,
  setShowUnsavedChangesModal: (show: boolean) => void,
) => {
  if (pendingTabChange) {
    setValue(pendingTabChange);
    localStorage.setItem('sector-tab', pendingTabChange);
    setPendingTabChange(null);
  }
  setShowUnsavedChangesModal(false);
};

export const handleCancelTabChange = async (
  sectorId: string,
  setLoading: (loading: boolean) => void,
  setSectorInfo: (info: any) => void,
  setIsEditingInfoTab: (editing: boolean) => void,
  setShowUnsavedChangesModal: (show: boolean) => void,
  pendingTabChange: string | null,
  setValue: (value: string) => void,
  setPendingTabChange: (value: string | null) => void,
) => {
  setLoading(true);
  try {
    const { data } = await apiClient.sector.sectorDetail(sectorId);
    setSectorInfo(data.sector);
    setIsEditingInfoTab(false);
    setShowUnsavedChangesModal(false);
    if (pendingTabChange) {
      setValue(pendingTabChange);
      localStorage.setItem('sector-tab', pendingTabChange);
      setPendingTabChange(null);
    }
  } catch (err) {
    console.log('Error canceling tab change', err);
  } finally {
    setLoading(false);
  }
};

export const getIrrigationValueFromSectorInfo = (
  sectorInfo: SectorDetailSchema | undefined,
  inheritedConfig: InheritedConfig,
  field: keyof IrrigationConfig,
) => {
  if (sectorInfo?.parentSector && sectorInfo.parentSector !== '1') {
    return inheritedConfig.irrigationConfig[field] || '';
  }
  const ownValue = (sectorInfo?.irrigationConfig as IrrigationConfig)?.[field];
  return ownValue || '';
};
